import { FC, ReactNode, useEffect, useMemo, useState } from 'react';
import QRCode from 'qrcode.react';

import { useAnalytics } from '../../../../../core/Analytics/AnalyticsProvider';
import { useSelectedProvider } from '../../../../App/ProviderContainer/useSelectedProvider';
import { buildMobileHandoffUrl } from '../../../../../routes';
import { useVersion } from '../../../../ApplicationProviders/VersionProvider/VersionProvider';
import { useProduct } from '../../../../ApplicationProviders/ProductProvider/ProductProvider';
import { HandoffCredentialsAction, HandoffSession } from '../../../../../models/handoffSession';
import { LoadingOverlay } from '../../../../../core/screens/LoadingOverlay/LoadingOverlay';
import { HandoffCodeScanned } from '../../../../../connectivity/screens/handoff/HandoffCodeScanned/HandoffCodeScanned';
import { CredentialsEntity } from '../../../../../models/credentials';
import { useHandoffSession as useHandoffSessionImport } from '../../../../../payments/legacy/components/PayHandoffDirector/hooks/useHandoffSession';
import { AwaitingQrCodeScreenWithInstructions } from '../../atoms/AwaitingQrCodeScreenWithInstructions';
import { PageView } from '../../../../../models/analytics';

export type HandoffAwaitingScreenProps = {
  heading: ReactNode;
  allowExit?: boolean;
  credentials?: CredentialsEntity;
  onPollerError: () => void;
  useHandoffSession?: typeof useHandoffSessionImport;
};

export const HandoffAwaitingScreenView: FC<HandoffAwaitingScreenProps> = ({
  heading,
  allowExit,
  credentials,
  onPollerError,
  useHandoffSession = useHandoffSessionImport,
}: HandoffAwaitingScreenProps) => {
  const {
    version: { version },
  } = useVersion();
  const { tinklinkAnalytics } = useAnalytics();
  const { productType, productDimension } = useProduct();
  const { selectedProvider } = useSelectedProvider();
  const [scanned, setScanned] = useState<boolean>(false);

  useEffect(() => {
    tinklinkAnalytics.sendViewEvent({
      view: PageView.BANKID_HANDOFF_SCREEN,
    });
  }, [tinklinkAnalytics]);

  // Use same instances of poller props to prevent useHandoffSession creating new session on any rerender
  const handoffSessionProps = useMemo(
    () => ({
      handlePollerError: () => onPollerError(),
      shouldStopPoller: ({ isConsumed }: HandoffSession) => {
        if (isConsumed) {
          setScanned(true);

          return true;
        }

        return false;
      },
    }),
    // Hack fix to approach this in a onMount approach
    // This whole component should be re-written and useHandoffSession should be deprecated/re-written
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  const { handoffSession } = useHandoffSession(handoffSessionProps);

  if (!handoffSession) {
    return <LoadingOverlay />;
  }

  if (scanned && credentials) {
    return <HandoffCodeScanned provider={selectedProvider} credentials={credentials} />;
  }

  const url = buildMobileHandoffUrl({
    version,
    productType,
    handoffSessionId: handoffSession.id,
    productDimension,
    handoffCredentialsAction: HandoffCredentialsAction.BANKID_AUTH,
  });

  return (
    <AwaitingQrCodeScreenWithInstructions
      provider={selectedProvider}
      heading={heading}
      qrCode={<QRCode data-testid="handoff-qr-code" value={url} size={220} includeMargin />}
      allowExit={Boolean(allowExit)}
    />
  );
};
